import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { Tabs, type TabsProps } from '@zoopla-eng/zds-components/tabs';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { SearchMedium } from '@zoopla-eng/zds-components/icons';
import {
    AddressSelection,
    AddressSelectionSuccessHandler
} from '@zoopla/valuation/shared';
import HousePricesAreaSearch from '../HousePricesAreaSearch/HousePricesAreaSearch';
import { type HousePricesSearchType, type HousePricesPage } from '../../types';
import { trackHousePricesSearch } from '../../helpers';
import {
    containerStyle,
    tabsWrapperStyle,
    tabStyle
} from './HousePricesSearchTabs.styles.css';
import {
    InputLocation,
    SearchBar,
    SearchButton,
    useSearchBarExperiment
} from '@zoopla/listing-search/listing-search-bar';
import { Box } from '@zoopla-eng/zds-components/box';
import { QueryType } from '@zoopla/listing-search/listing-search-bar';

type SearchTabs = TabsProps<HousePricesSearchType>['tabs'];
type SearchTabProps = {
    parent: HousePricesPage;
};

const ADDRESS_SELECTION_ID = 'house-prices-search-hero-address';

const PropertySearchTab: FC<SearchTabProps> = ({ parent }) => {
    const router = useRouter();

    const handleSuccess: AddressSelectionSuccessHandler = ({ uprn }) => {
        router.push(`/property/uprn/${uprn}/`);
        trackHousePricesSearch(parent, 'property');
    };

    return (
        <AddressSelection
            id={ADDRESS_SELECTION_ID}
            onSuccess={handleSuccess}
            addressCtaOverrides={{
                text: 'Search',
                Icon: SearchMedium
            }}
            ctaAlignment={{
                default: 'fill',
                bpS200: 'left'
            }}
        />
    );
};

const AreaSearchTab: FC<SearchTabProps> = ({ parent }) => {
    const isUsingSearchBar = useSearchBarExperiment();

    if (!isUsingSearchBar) {
        return (
            <HousePricesAreaSearch
                layout="unstacked"
                parent={parent}
                initialiseSelectedItem
            />
        );
    }

    // The tracking type depends on where the search occurs which was set up
    // as part of the useSearchBarExperiment. It's not entirely clear what the
    // distinction is here, so if this is codified an explanation should be
    // added.
    const trackingType = parent === 'landing' ? 'search' : 'emptySearch';

    return (
        <SearchBar
            type="house-prices"
            tracking={{
                [trackingType]: () => {
                    trackHousePricesSearch(parent, 'area');
                }
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                rowGap="S400"
                width="100%"
            >
                <Box flexGrow={1}>
                    <InputLocation
                        id="header-location"
                        queryType={QueryType.AddressPostcodes}
                    />
                </Box>
                <Box>
                    <SearchButton />
                </Box>
            </Box>
        </SearchBar>
    );
};

// Tabs are generated based on extra props, so we have this type to enable us
// to safely go from the array of props to the tabs themselves to satisfy the
// Generic used in Tab (from TabsProps).
type TabProps = Pick<SearchTabs[number], 'value' | 'label'> & {
    SearchTab: FC<SearchTabProps>;
    content: string;
};

const TABS: TabProps[] = [
    {
        value: 'area',
        label: 'Area search',
        content: 'Check sold prices in a city, town or street.',
        SearchTab: AreaSearchTab
    },
    {
        value: 'property',
        label: 'Property search',
        content: 'See how much it last sold for and what it’s worth today.',
        SearchTab: PropertySearchTab
    }
];

type GenerateTabOptions = {
    showContent: boolean;
    parent: HousePricesPage;
    hasCardContainer: boolean;
};

const generateTabs = (options: GenerateTabOptions): SearchTabs => {
    const { hasCardContainer, showContent, parent } = options;

    return TABS.map(({ value, label, content, SearchTab }) => {
        const component = (
            <div
                className={tabStyle({
                    hasCardContainer,
                    showContent
                })}
            >
                {showContent && <p className={fontGroups.ladle}>{content}</p>}
                <SearchTab parent={parent} />
            </div>
        );
        return {
            value,
            label,
            component
        };
    });
};

type HousePricesSearchTabsProps = {
    defaultTab: HousePricesSearchType;
    showContent?: boolean;
    parent: HousePricesPage;
    hasCardContainer?: boolean;
};

const HousePricesSearchTabs: FC<HousePricesSearchTabsProps> = ({
    showContent = false,
    defaultTab,
    hasCardContainer = false,
    parent
}) => {
    const [tabValue, setTabValue] = useState(defaultTab);

    const tabs = generateTabs({
        showContent,
        parent,
        hasCardContainer
    });

    return (
        <div className={containerStyle({ hasCardContainer })}>
            <div className={tabsWrapperStyle}>
                <Tabs
                    label="Search for information on a specific property or information on an area"
                    value={tabValue}
                    onValueChange={(value) => {
                        setTabValue(value);
                    }}
                    tabs={tabs}
                    alignment={{
                        default: 'centered',
                        bpS200: hasCardContainer ? 'left' : 'fill'
                    }}
                />
            </div>
        </div>
    );
};

export default HousePricesSearchTabs;
